import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Navbar } from "../../components/layout/Navbar";
import { Footer } from "../../components/layout/Footer";
import { useCart } from "../../context/CartContext";
import { useAuth } from "../../context/AuthContext";
import { useLanguage } from "../../context/LanguageContext";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { ShoppingCart, Trash2, Plus, Minus, Package, ArrowRight, ArrowLeft, Building2, Phone, User, MapPin, Truck, Store } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DELIVERY_TYPES = [
  { id: "delivery", labelAr: "توصيل للعنوان", labelEn: "Delivery to Address", icon: Truck },
  { id: "pickup", labelAr: "استلام من المخزن", labelEn: "Pickup from Warehouse", icon: Store },
];

const CartPage = () => {
  const navigate = useNavigate();
  const { items, updateQuantity, removeItem, getTotal, clearCart } = useCart();
  const { user, isAuthenticated, getAuthHeaders } = useAuth();
  const { t, isRTL } = useLanguage();
  const [isCheckingOut, setIsCheckingOut] = useState(false);
  
  // Guest checkout fields
  const [guestInfo, setGuestInfo] = useState({
    fullName: "",
    companyName: "",
    phone: "",
    address: "",
    notes: ""
  });
  
  // Order options
  const [deliveryType, setDeliveryType] = useState("delivery");

  const ArrowIcon = isRTL ? ArrowLeft : ArrowRight;

  const handleGuestInfoChange = (field, value) => {
    setGuestInfo(prev => ({ ...prev, [field]: value }));
  };

  const validateGuestInfo = () => {
    if (!guestInfo.fullName.trim()) {
      toast.error(t("pleaseEnterName"));
      return false;
    }
    if (!guestInfo.companyName.trim()) {
      toast.error(t("pleaseEnterCompany"));
      return false;
    }
    if (!guestInfo.phone.trim()) {
      toast.error(t("pleaseEnterPhone"));
      return false;
    }
    if (deliveryType === "delivery" && !guestInfo.address.trim()) {
      toast.error(t("pleaseEnterAddress"));
      return false;
    }
    return true;
  };

  const handleCheckout = async () => {
    if (items.length === 0) {
      toast.error(t("cartIsEmpty"));
      return;
    }

    // Validate based on authentication status
    if (!isAuthenticated) {
      if (!validateGuestInfo()) return;
    } else {
      if (deliveryType === "delivery" && !guestInfo.address.trim()) {
        toast.error(t("pleaseEnterAddress"));
        return;
      }
    }

    setIsCheckingOut(true);

    try {
      const orderData = {
        customer_id: isAuthenticated ? user.id : "guest",
        customer_name: isAuthenticated ? (user.full_name || user.username) : guestInfo.fullName,
        customer_company: isAuthenticated ? (user.company || "") : guestInfo.companyName,
        customer_phone: isAuthenticated ? (user.phone || guestInfo.phone) : guestInfo.phone,
        customer_address: deliveryType === "delivery" ? guestInfo.address : "استلام من المخزن",
        items: items.map(item => ({
          product_id: item.id,
          product_name: item.name,
          quantity: item.quantity,
          price: item.sale_price,
          total: item.sale_price * item.quantity
        })),
        subtotal: getTotal(),
        delivery_fee: 0, // Will be set by admin later
        delivery_type: deliveryType,
        total: getTotal(), // Delivery fee added later by admin
        notes: guestInfo.notes,
        status: "pending",
        is_guest_order: !isAuthenticated
      };

      const headers = isAuthenticated ? getAuthHeaders() : { "Content-Type": "application/json" };
      
      await axios.post(`${API}/orders/guest`, orderData, { headers });

      toast.success(t("orderPlacedSuccessfully"));
      clearCart();
      
      if (isAuthenticated) {
        navigate("/orders");
      } else {
        navigate("/");
        toast.success(t("orderConfirmationSent"));
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || t("failedToPlaceOrder"));
    } finally {
      setIsCheckingOut(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f8fafc]">
      <Navbar />
      
      <div className="container mx-auto px-4 md:px-8 max-w-7xl py-8" data-testid="cart-page">
        <div className="mb-8">
          <h1 className="font-heading text-3xl font-bold mb-2">{t("shoppingCart")}</h1>
          <p className="text-muted-foreground">
            {items.length} {t("itemsInCart")}
          </p>
        </div>

        {items.length === 0 ? (
          <Card className="border-0 shadow-sm">
            <CardContent className="py-16 text-center">
              <ShoppingCart className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
              <h3 className="font-heading font-semibold text-lg mb-2">{t("cartIsEmpty")}</h3>
              <p className="text-muted-foreground mb-6">{t("startAddingProducts")}</p>
              <Link to="/products">
                <Button className="bg-[#1a365d]">{t("browseProducts")}</Button>
              </Link>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
            {/* Cart Items */}
            <div className="lg:col-span-2 space-y-4">
              {items.map((item) => (
                <Card key={item.id} className="border-0 shadow-sm">
                  <CardContent className="p-4 sm:p-6">
                    <div className="flex gap-3 sm:gap-4">
                      {/* Product Image */}
                      <div className="w-20 h-20 sm:w-24 sm:h-24 bg-slate-100 rounded-lg overflow-hidden flex-shrink-0">
                        {item.images?.[0] ? (
                          <img
                            src={item.images[0]}
                            alt={item.name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Package className="w-6 h-6 sm:w-8 sm:h-8 text-slate-300" />
                          </div>
                        )}
                      </div>

                      {/* Product Info */}
                      <div className="flex-1 min-w-0">
                        <Link to={`/products/${item.id}`}>
                          <h3 className="font-semibold text-sm sm:text-base hover:text-[#1a365d] transition-colors line-clamp-2">
                            {item.name}
                          </h3>
                        </Link>
                        <p className="text-xs sm:text-sm text-muted-foreground">{item.brand}</p>
                        <p className="text-base sm:text-lg font-bold text-[#1a365d] mt-1 sm:mt-2">
                          {item.sale_price} {t("lyd")}
                        </p>
                        
                        {/* Mobile: Quantity and Delete in same row */}
                        <div className="flex items-center justify-between mt-2 sm:hidden">
                          <div className="flex items-center gap-1">
                            <Button
                              variant="outline"
                              size="icon"
                              className="h-7 w-7"
                              onClick={() => updateQuantity(item.id, item.quantity - 1)}
                              disabled={item.quantity <= 1}
                            >
                              <Minus className="h-3 w-3" />
                            </Button>
                            <span className="w-8 text-center font-semibold text-sm">{item.quantity}</span>
                            <Button
                              variant="outline"
                              size="icon"
                              className="h-7 w-7"
                              onClick={() => updateQuantity(item.id, item.quantity + 1)}
                            >
                              <Plus className="h-3 w-3" />
                            </Button>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-500 hover:text-red-600 hover:bg-red-50 h-7 px-2"
                            onClick={() => removeItem(item.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>

                      {/* Desktop: Quantity Controls */}
                      <div className="hidden sm:flex flex-col items-end gap-2">
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => updateQuantity(item.id, item.quantity - 1)}
                            disabled={item.quantity <= 1}
                          >
                            <Minus className="h-4 w-4" />
                          </Button>
                          <span className="w-12 text-center font-semibold">{item.quantity}</span>
                          <Button
                            variant="outline"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => updateQuantity(item.id, item.quantity + 1)}
                          >
                            <Plus className="h-4 w-4" />
                          </Button>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-500 hover:text-red-600 hover:bg-red-50"
                          onClick={() => removeItem(item.id)}
                        >
                          <Trash2 className="h-4 w-4 mr-1" />
                          {t("remove")}
                        </Button>
                      </div>
                    </div>

                    {/* Item Total */}
                    <div className="mt-3 sm:mt-4 pt-3 sm:pt-4 border-t flex justify-between items-center">
                      <span className="text-muted-foreground text-sm">{t("subtotal")}</span>
                      <span className="font-bold">{(item.sale_price * item.quantity).toFixed(2)} {t("lyd")}</span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Order Summary */}
            <div className="lg:col-span-1">
              <Card className="border-0 shadow-sm lg:sticky lg:top-24">
                <CardHeader className="pb-2 sm:pb-4">
                  <CardTitle className="font-heading text-lg sm:text-xl">{t("orderSummary")}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3 sm:space-y-4">
                  {/* Items Summary */}
                  <div className="space-y-2">
                    {items.map((item) => (
                      <div key={item.id} className="flex justify-between text-sm">
                        <span className="text-muted-foreground">
                          {item.name} x {item.quantity}
                        </span>
                        <span>{(item.sale_price * item.quantity).toFixed(2)}</span>
                      </div>
                    ))}
                  </div>

                  <div className="border-t pt-4">
                    <div className="flex justify-between mb-2">
                      <span className="text-muted-foreground">{t("subtotal")}</span>
                      <span>{getTotal().toFixed(2)} {t("lyd")}</span>
                    </div>
                    <div className="flex justify-between mb-2">
                      <span className="text-muted-foreground">{t("deliveryFee")}</span>
                      <span className="text-slate-500">{isRTL ? "يحدد لاحقاً" : "TBD"}</span>
                    </div>
                    <div className="flex justify-between text-lg font-bold border-t pt-2">
                      <span>{t("productTotal")}</span>
                      <span className="text-[#1a365d]">{getTotal().toFixed(2)} {t("lyd")}</span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      {isRTL ? "* سيتم تحديد رسوم التوصيل بعد تأكيد الطلب" : "* Delivery fee will be set after order confirmation"}
                    </p>
                  </div>

                  {/* Delivery Type Selection */}
                  <div className="pt-3 sm:pt-4 border-t space-y-3">
                    <h4 className="font-semibold text-sm">{t("deliveryType")}</h4>
                    <div className="grid grid-cols-2 gap-2">
                      {DELIVERY_TYPES.map((type) => {
                        const Icon = type.icon;
                        return (
                          <button
                            key={type.id}
                            onClick={() => setDeliveryType(type.id)}
                            className={`flex flex-col items-center gap-1 sm:gap-2 p-3 sm:p-4 rounded-lg border transition-colors ${
                              deliveryType === type.id 
                                ? 'border-[#1a365d] bg-[#1a365d]/5' 
                                : 'border-slate-200 hover:border-slate-300'
                            }`}
                            data-testid={`delivery-type-${type.id}`}
                          >
                            <Icon className={`h-5 w-5 sm:h-6 sm:w-6 ${deliveryType === type.id ? 'text-[#1a365d]' : 'text-slate-400'}`} />
                            <span className="text-xs text-center leading-tight">
                              {isRTL ? type.labelAr : type.labelEn}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Customer Information Section */}
                  <div className="pt-3 sm:pt-4 border-t space-y-3 sm:space-y-4">
                    <h4 className="font-semibold text-sm">{t("customerInfo")}</h4>
                    
                    {/* Show name/company/phone fields for guests */}
                    {!isAuthenticated && (
                      <>
                        <div>
                          <Label className="text-sm mb-1 block">{t("fullName")} *</Label>
                          <div className="relative">
                            <User className={`absolute ${isRTL ? 'right-3' : 'left-3'} top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground`} />
                            <Input
                              value={guestInfo.fullName}
                              onChange={(e) => handleGuestInfoChange("fullName", e.target.value)}
                              placeholder={t("enterFullName")}
                              className={`${isRTL ? 'pr-10' : 'pl-10'}`}
                            />
                          </div>
                        </div>

                        <div>
                          <Label className="text-sm mb-1 block">{t("companyName")} *</Label>
                          <div className="relative">
                            <Building2 className={`absolute ${isRTL ? 'right-3' : 'left-3'} top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground`} />
                            <Input
                              value={guestInfo.companyName}
                              onChange={(e) => handleGuestInfoChange("companyName", e.target.value)}
                              placeholder={t("enterCompanyName")}
                              className={`${isRTL ? 'pr-10' : 'pl-10'}`}
                            />
                          </div>
                        </div>

                        <div>
                          <Label className="text-sm mb-1 block">{t("phone")} *</Label>
                          <div className="relative">
                            <Phone className={`absolute ${isRTL ? 'right-3' : 'left-3'} top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground`} />
                            <Input
                              value={guestInfo.phone}
                              onChange={(e) => handleGuestInfoChange("phone", e.target.value)}
                              placeholder="+218 9x xxx xxxx"
                              className={`${isRTL ? 'pr-10' : 'pl-10'}`}
                            />
                          </div>
                        </div>
                      </>
                    )}

                    {/* Logged in user info display */}
                    {isAuthenticated && (
                      <div className="bg-slate-50 rounded-lg p-3 space-y-2">
                        <div className="flex items-center gap-2 text-sm">
                          <User className="h-4 w-4 text-muted-foreground" />
                          <span>{user?.full_name || user?.username}</span>
                        </div>
                        {user?.phone && (
                          <div className="flex items-center gap-2 text-sm">
                            <Phone className="h-4 w-4 text-muted-foreground" />
                            <span>{user.phone}</span>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Shipping Address - Only for delivery type */}
                    {deliveryType === "delivery" && (
                      <div>
                        <Label className="text-sm mb-1 block">{t("shippingAddress")} *</Label>
                        <div className="relative">
                          <MapPin className={`absolute ${isRTL ? 'right-3' : 'left-3'} top-3 h-4 w-4 text-muted-foreground`} />
                          <textarea
                            value={guestInfo.address}
                            onChange={(e) => handleGuestInfoChange("address", e.target.value)}
                            placeholder={t("enterShippingAddress")}
                            className={`w-full h-20 px-3 py-2 border rounded-md resize-none focus:outline-none focus:ring-2 focus:ring-[#1a365d] ${isRTL ? 'pr-10' : 'pl-10'}`}
                          />
                        </div>
                      </div>
                    )}

                    {/* Notes */}
                    <div>
                      <Label className="text-sm mb-1 block">{t("notes")}</Label>
                      <Input
                        value={guestInfo.notes}
                        onChange={(e) => handleGuestInfoChange("notes", e.target.value)}
                        placeholder={t("orderNotes")}
                      />
                    </div>
                  </div>

                  {/* Checkout Button */}
                  <Button
                    className="w-full bg-[#1a365d] hover:bg-[#1a365d]/90 h-12 text-base font-semibold"
                    onClick={handleCheckout}
                    disabled={isCheckingOut || items.length === 0}
                    data-testid="checkout-btn"
                  >
                    {isCheckingOut ? t("processing") : t("placeOrder")}
                    <ArrowIcon className={`${isRTL ? 'mr-2' : 'ml-2'} h-5 w-5`} />
                  </Button>

                  {!isAuthenticated && (
                    <p className="text-sm text-center text-muted-foreground">
                      {t("haveAccount")}{" "}
                      <Link to="/login" className="text-[#1a365d] hover:underline font-medium">
                        {t("login")}
                      </Link>
                    </p>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default CartPage;
